import { z } from 'zod';
import { isCivilDate } from './date.js';
import { weatherConditions } from './weather.js';
export const civilDate = z.string().refine(isCivilDate, 'Erwartet YYYY-MM-DD');
/** ISO-8601 instant, e.g. '2026-08-22T15:00:00.000Z'. Used for events only. */
export const instant = z.string().datetime();
export const hue = z.number().int().min(0).max(360);
// ---------------------------------------------------------------- members
export const memberSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(40),
    hue,
    sortOrder: z.number().int(),
});
export const memberInputSchema = memberSchema.omit({ id: true, sortOrder: true }).partial({ hue: true });
// ------------------------------------------------------------- categories
export const categorySchema = z.object({
    id: z.string(),
    label: z.string().min(1).max(40),
    hue,
    defaultShelfDays: z.number().int().min(0).max(3650),
});
// ---------------------------------------------------------------- items
export const itemStatus = z.enum(['active', 'consumed', 'discarded']);
export const storageLocation = z.enum(['fridge', 'freezer', 'pantry']);
export const itemSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(80),
    barcode: z.string().nullable(),
    imageUrl: z.string().nullable(),
    categoryId: z.string().nullable(),
    location: storageLocation,
    /** null == 'kein Datum' */
    bestBefore: civilDate.nullable(),
    /** Set when the pack is opened; shortens the effective best-before. */
    openedAt: civilDate.nullable(),
    /** Snapshot of the shelf life at insert time — see shelfDaysFor(). */
    shelfDays: z.number().int().min(0),
    qty: z.number().int().min(0).max(999),
    status: itemStatus,
    addedAt: instant,
    consumedAt: instant.nullable(),
});
/** Either an absolute http(s) URL or a same-origin path like /api/…/image. */
export const imageRef = z
    .string()
    .max(2048)
    .refine((v) => v.startsWith('/') || /^https?:\/\//.test(v), 'Erwartet URL oder /pfad');
export const itemCreateSchema = z.object({
    name: z.string().min(1).max(80),
    barcode: z.string().max(32).nullable().optional(),
    imageUrl: imageRef.nullable().optional(),
    categoryId: z.string().nullable().optional(),
    location: storageLocation.default('fridge'),
    bestBefore: civilDate.nullable().optional(),
    qty: z.number().int().min(1).max(999).default(1),
});
export const itemPatchSchema = z.object({
    name: z.string().min(1).max(80).optional(),
    bestBefore: civilDate.nullable().optional(),
    openedAt: civilDate.nullable().optional(),
    qty: z.number().int().min(0).max(999).optional(),
    location: storageLocation.optional(),
    categoryId: z.string().nullable().optional(),
});
// ------------------------------------------------------------- shopping
export const shoppingSource = z.enum(['local', 'bring', 'auto']);
export const shoppingItemSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(80),
    done: z.boolean(),
    /** Surfaced so the list can say where a row came from — see 'auto'. */
    source: shoppingSource,
    addedAt: instant,
});
// ------------------------------------------------------- shopping rules
export const shoppingRuleSchema = z.object({
    id: z.string(),
    label: z.string().min(1).max(80),
    matchKey: z.string(),
    barcode: z.string().nullable(),
    minQty: z.number().int().min(1).max(99),
    armed: z.boolean(),
    enabled: z.boolean(),
    lastTriggeredAt: z.string().nullable(),
    /** How many are in the fridge right now — so a rule that matches nothing is obvious. */
    stock: z.number().int(),
});
export const shoppingRuleInputSchema = z.object({
    label: z.string().min(1).max(80),
    barcode: z.string().max(64).nullable().optional(),
    minQty: z.number().int().min(1).max(99).default(1),
});
// --------------------------------------------------------------- events
export const eventSource = z.enum(['local', 'ics']);
export const eventSchema = z.object({
    id: z.string(),
    title: z.string().min(1).max(120),
    startsAt: instant,
    endsAt: instant.nullable(),
    /** null == 'Alle'. The prototype modelled this as a member row; it is a flag. */
    memberId: z.string().nullable(),
    source: eventSource,
    externalId: z.string().nullable(),
    feedId: z.string().nullable(),
    allDay: z.boolean(),
});
// -------------------------------------------------------- calendar feeds
/**
 * What kind of thing a feed carries.
 *
 * Not a boolean, because a third answer is already visible: Feiertage and
 * Schulferien belong in the grid but should read more quietly than a family
 * appointment.
 */
export const feedRole = z.enum(['family', 'waste']);
export const calendarFeedSchema = z.object({
    id: z.string(),
    label: z.string(),
    /**
     * Where the calendar came from. A `file:` scheme means it was uploaded, not
     * subscribed to — there is nothing behind it to re-fetch.
     */
    url: z.string(),
    role: feedRole,
    memberId: z.string().nullable(),
    enabled: z.boolean(),
    lastSyncedAt: z.string().nullable(),
    lastError: z.string().nullable(),
    lastEventCount: z.number().int().nullable(),
});
// -------------------------------------------------------------- products
export const productSchema = z.object({
    barcode: z.string(),
    name: z.string(),
    brand: z.string().nullable(),
    imageUrl: z.string().nullable(),
    categoryId: z.string().nullable(),
    defaultShelfDays: z.number().int().nullable(),
    source: z.enum(['off', 'manual']),
});
// ----------------------------------------------------------------- notes
/** Coordinates are 0..1 of the board, so the page survives a resize. */
export const strokePointSchema = z.object({
    x: z.number().min(-0.1).max(1.1),
    y: z.number().min(-0.1).max(3),
    /** Pen pressure 0..1; absent for finger and mouse. */
    p: z.number().min(0).max(1).optional(),
});
export const strokeSchema = z.object({
    color: z.string().max(64),
    /** Base width in board units (1 = full board width). */
    width: z.number().min(0.0001).max(0.1),
    points: z.array(strokePointSchema).min(1).max(4000),
});
export const notesSchema = z.object({
    strokes: z.array(strokeSchema).max(2000),
    updatedAt: z.string(),
});
// -------------------------------------------------------------- settings
export const settingsSchema = z.object({
    /** False on a fresh install, which is what triggers the setup wizard. */
    setupComplete: z.boolean().default(false),
    timeZone: z.string().default('Europe/Berlin'),
    calendarSync: z.boolean().default(false),
    voiceCapture: z.boolean().default(true),
    childLock: z.boolean().default(false),
    nightDim: z.boolean().default(true),
});
// ---------------------------------------------------- server-sent events
export const weatherCondition = z.enum(weatherConditions);
/** One place, as returned by the geocoder — what the location picker lists. */
export const weatherPlaceSchema = z.object({
    name: z.string(),
    /** "Hessen, Deutschland" — enough to tell two Kassels apart. */
    region: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    timezone: z.string(),
});
export const weatherHourSchema = z.object({
    /** Local wall clock, "15:00" — Open-Meteo is asked for the place's own zone. */
    time: z.string(),
    tempC: z.number(),
    precipChance: z.number().int().min(0).max(100),
    condition: weatherCondition,
});
export const weatherDaySchema = z.object({
    date: civilDate,
    /** "Mo", or "Heute" for the first entry. */
    label: z.string(),
    condition: weatherCondition,
    conditionLabel: z.string(),
    maxC: z.number(),
    minC: z.number(),
});
export const weatherReportSchema = z.object({
    place: z.string(),
    tempC: z.number(),
    feelsLikeC: z.number(),
    condition: weatherCondition,
    conditionLabel: z.string(),
    isDay: z.boolean(),
    /** The actionable bit: "Regen ab 15 Uhr". Null when nothing is about to change. */
    outlook: z.string().nullable(),
    hours: z.array(weatherHourSchema),
    days: z.array(weatherDaySchema),
});
export const weatherStatusSchema = z.object({
    configured: z.boolean(),
    place: weatherPlaceSchema.nullable(),
    report: weatherReportSchema.nullable(),
    /** When the cached report was fetched — the widget shows this when stale. */
    fetchedAt: instant.nullable(),
    lastError: z.string().nullable(),
});
export const streamEvent = z.enum(['items', 'shopping', 'events', 'members', 'settings', 'notes', 'scan', 'weather', 'rules', 'tick']);
/** True when a feed was uploaded as a file rather than subscribed to by URL. */
export function isUploadedFeed(feed) {
    return feed.url.startsWith('file:');
}
